import { useEffect, useState } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, query } from 'firebase/firestore';
import { useAdminGuard } from '../../hooks/useAdminGuard';

const Pledges = () => {
  const { user, loading, isAdmin } = useAdminGuard({ redirectIfNotAdmin: true });
  const [pledges, setPledges] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState<'date' | 'amount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!user || !isAdmin) return;
    const fetchPledges = async () => {
      const q = query(collection(db, 'pledges'));
      const querySnapshot = await getDocs(q);
      const pledgesList: any[] = [];
      querySnapshot.forEach((docSnap) => {
        pledgesList.push({ ...(docSnap.data()), id: docSnap.id });
      });
      setPledges(pledgesList);
    };
    fetchPledges();
  }, [user, isAdmin]);

  // Filtering, searching, sorting
  let filtered = pledges.filter(p =>
    (!typeFilter || p.donationType === typeFilter) &&
    (!search ||
      p.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.phone?.toLowerCase().includes(search.toLowerCase())
    )
  );
  if (sort === 'date') {
    filtered = filtered.sort((a, b) => {
      const aDate = a.pledgedAt?.toDate ? a.pledgedAt.toDate() : new Date(a.pledgedAt);
      const bDate = b.pledgedAt?.toDate ? b.pledgedAt.toDate() : new Date(b.pledgedAt);
      return sortDir === 'asc' ? aDate - bDate : bDate - aDate;
    });
  } else if (sort === 'amount') {
    filtered = filtered.sort((a, b) => {
      const aAmt = parseFloat((a.pledgeAmount || '').replace(/[^\d.]/g, '')) || 0;
      const bAmt = parseFloat((b.pledgeAmount || '').replace(/[^\d.]/g, '')) || 0;
      return sortDir === 'asc' ? aAmt - bAmt : bAmt - aAmt;
    });
  }

  if (loading || !user || !isAdmin) return (
    <div className="flex justify-center items-center h-40">
      <svg className="animate-spin h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span className="ml-3 text-teal-700 text-sm">Loading pledges...</span>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto py-6 px-1 sm:py-10 sm:px-4">
      <h1 className="text-xl xs:text-2xl sm:text-3xl font-bold text-teal-700 mb-4 xs:mb-6 sm:mb-8">Pledge Management</h1>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by name, email, or phone"
          className="border border-stone-300 rounded px-3 py-2 text-sm w-full sm:w-64"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="border border-stone-300 rounded px-3 py-2 text-sm w-full sm:w-48"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="One-Time">One-Time</option>
          <option value="Recurring">Recurring</option>
        </select>
        <div className="flex gap-2 items-center">
          <label className="text-xs text-stone-500">Sort by:</label>
          <select
            className="border border-stone-300 rounded px-2 py-1 text-xs"
            value={sort}
            onChange={e => setSort(e.target.value as 'date' | 'amount')}
          >
            <option value="date">Date</option>
            <option value="amount">Amount</option>
          </select>
          <button
            className="ml-1 px-2 py-1 rounded border border-stone-300 text-xs hover:bg-stone-100"
            onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg shadow border border-stone-200 bg-white">
        <table className="min-w-[500px] xs:min-w-[600px] sm:min-w-[700px] md:min-w-full divide-y divide-stone-200 text-[11px] xs:text-xs sm:text-sm">
          <thead className="bg-stone-50">
            <tr>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Name</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Email</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Phone</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Amount</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Type</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Date</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Reminder</th>
              <th className="px-2 py-2 sm:px-4 sm:py-3 text-left font-medium text-stone-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-stone-100">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-stone-50">
                <td className="px-2 py-2 sm:px-4 sm:py-3 font-medium text-stone-800 break-all">{p.fullName}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-600 break-all">{p.email}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-600 break-all">
                  <a href={`tel:${p.phone}`} className="text-teal-700 hover:underline">{p.phone}</a>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{p.pledgeAmount}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">{p.donationType}</td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 text-stone-700">
                  {p.pledgedAt?.toDate ? p.pledgedAt.toDate().toLocaleString() : new Date(p.pledgedAt).toLocaleString()}
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3">
                  {p.reminder ? <span className="inline-block px-2 py-1 rounded bg-teal-100 text-teal-700 text-xs">Yes</span> : <span className="inline-block px-2 py-1 rounded bg-stone-100 text-stone-500 text-xs">No</span>}
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 flex gap-2">
                  <a href={`mailto:${p.email}`} className="text-blue-600 hover:underline text-xs sm:text-sm">Email</a>
                  <a href={`tel:${p.phone}`} className="text-teal-700 hover:underline text-xs sm:text-sm">Call</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Pledges; 